/**
 * Draft-gates — gedeelde herkenning van berichten waarvoor we geen AI-draft maken.
 *
 * Gedeeld tussen backend (api/_lib/draft-gates.ts re-exporteert dit bestand,
 * zelfde patroon als bizziphone-parse) en frontend (ThreadDetail verbergt de
 * AI-sectie bij een herkend automatisch bericht). Dit is géén oordeel over
 * benodigde opvolging en geen bewijs dat de mail is afgehandeld.
 *
 * De 158 bounce-drafts in 30 dagen ontstonden doordat elke caller zijn eigen
 * filters had. De gate zit in draft-generator.ts zelf en beschermt daarmee
 * álle huidige en toekomstige paden (on-demand, prebake, cron, Brein).
 */

export const NO_REPLY_EMAIL_PATTERNS: RegExp[] = [
  /^no-?_?reply@/i,
  /^donotreply@/i,
  /^do-not-reply@/i,
  /^mailer-daemon@/i,
  /^postmaster@/i,
  /no-?_?reply[^@]*@/i,
  /donotreply[^@]*@/i,
];

export const AUTOREPLY_SUBJECT_PATTERNS: RegExp[] = [
  // Alleen expliciete automatische onderwerpvormen, niet losse woorden in
  // klantvragen. Re:/FW: bewust niet strippen: dat kan menselijke opvolging zijn.
  /^undeliverable(?:\s*:|$)/i,
  /^delivery status notification(?:\s*\((?:failure|delay|success)\))?(?:\s*:|$)/i,
  /^mail delivery failed(?:\s*:|$)/i,
  /^onbestelbaar(?:\s*:|$)/i,
  /^automatisch antwoord(?:\s*:|$)/i,
  /^automatic reply(?:\s*:|$)/i,
  /^auto[- ]?reply(?:\s*:|$)/i,
  /^out of office(?:\s*:|$)/i,
];

export interface DraftGateConversation {
  status?: string | null;
  from_email?: string | null;
  subject?: string | null;
  snippet?: string | null;
  customer_id?: string | null;
  message_count?: number | null;
}

export interface DraftGateMessage {
  is_incoming?: boolean | null;
  from_email?: string | null;
  reply_to_email?: string | null;
  subject?: string | null;
  body_text?: string | null;
  body_html?: string | null;
  has_attachments?: boolean | null;
}

export type SluitenZonderAntwoordReden = 'automatisch_antwoord' | 'nieuwsbrief_marketing';

const isVeiligAutomatischAntwoord = (subject: string): boolean =>
  /^(?:automatisch antwoord|automatic reply|auto[- ]?reply|out of office)(?:\s*:|$)/i.test(subject.trim());

/**
 * Conservatieve, handmatige afdoeningshint voor duidelijk niet-klantverkeer.
 * Dit is geen automatisch sluitbesluit. No-reply op zichzelf is onvoldoende:
 * transactionele meldingen kunnen nog operationele opvolging vragen.
 */
export function redenVoorSluitenZonderAntwoord(
  conversation: DraftGateConversation,
  targetMessage?: DraftGateMessage | null,
): SluitenZonderAntwoordReden | null {
  const subject = (targetMessage?.subject || conversation.subject || '').trim();
  const enkelLosNietKlantbericht = conversation.message_count === 1
    && !conversation.customer_id
    && targetMessage?.is_incoming === true
    && targetMessage.has_attachments !== true
    && !(typeof targetMessage.reply_to_email === 'string' && targetMessage.reply_to_email.trim());
  if (!enkelLosNietKlantbericht) return null;
  if (isVeiligAutomatischAntwoord(subject)) return 'automatisch_antwoord';

  const html = targetMessage?.body_html || '';
  const bodyText = targetMessage?.body_text || '';
  const links = html.match(/<a\b[^>]*href\s*=/gi)?.length || 0;
  const concreteUitschrijflink = /<a\b[^>]*href\s*=\s*["'][^"']*(?:unsubscribe|uitschrijven|afmelden|opt[%-]?out)[^"']*["'][^>]*>/i.test(html)
    || /<a\b[^>]*href\s*=\s*["'][^"']+["'][^>]*>[^<]*(?:uitschrijven|afmelden|unsubscribe|opt[ -]?out)[^<]*<\/a>/i.test(html);
  const bulkHtml = html.length >= 1_500 && /<table\b/i.test(html) && /<img\b/i.test(html) && links >= 5;
  const isAntwoordOfDoorstuur = /^(?:re|fw|fwd|aw)\s*:/i.test(subject);
  const lijktPersoonlijkeVraag = /\?/.test(subject)
    || /\b(?:ik|mijn|kunnen jullie|kunt u|kan ik|i|my|could you|can you)\b[^?\n]{0,160}\?/i.test(bodyText);

  if (concreteUitschrijflink && bulkHtml && !isAntwoordOfDoorstuur && !lijktPersoonlijkeVraag
    && !isRelayResolved(conversation, targetMessage)) {
    return 'nieuwsbrief_marketing';
  }
  return null;
}

export function isNoReplyEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return NO_REPLY_EMAIL_PATTERNS.some(p => p.test(email.trim()));
}

export function isAutoreplySubject(subject: string | null | undefined): boolean {
  if (!subject) return false;
  return AUTOREPLY_SUBJECT_PATTERNS.some(p => p.test(subject.trim()));
}

/**
 * Herkent een gesprek waar de inbox-sync de échte klant al uit een lead-gen-
 * relay heeft gevist (bv. no_reply@tapijtreiniging-ploeg.nl / Drijfveer): het
 * bericht komt van een no-reply-adres, maar het gesprek draagt een afwijkend,
 * echt klantadres. Zo'n gesprek verdient wél een draft — gericht aan de klant.
 * Generiek op de sync-handtekening, dus elke toekomstige relay doet vanzelf mee.
 */
export function isRelayResolved(
  conversation: DraftGateConversation,
  targetMessage?: DraftGateMessage | null
): boolean {
  const msgFrom = (targetMessage?.from_email || '').trim().toLowerCase();
  const convFrom = (conversation.from_email || '').trim().toLowerCase();
  return Boolean(
    msgFrom && convFrom && msgFrom !== convFrom
    && isNoReplyEmail(msgFrom) && !isNoReplyEmail(convFrom)
  );
}

/**
 * Retourneert een leesbare skip-reden, of null als de generator mag beoordelen.
 * Een skip zegt niets over eventuele acties of de afhandelstatus.
 */
export function shouldSkipDraft(
  conversation: DraftGateConversation,
  targetMessage?: DraftGateMessage | null
): string | null {
  if (conversation.status === 'bounce') {
    return 'bounce-gesprek';
  }
  const fromEmail = targetMessage?.from_email || conversation.from_email;
  if (isNoReplyEmail(fromEmail) && !isRelayResolved(conversation, targetMessage)) {
    return `no-reply-afzender (${fromEmail})`;
  }
  const subject = targetMessage?.subject || conversation.subject;
  if (isAutoreplySubject(subject)) {
    return `autoreply/bounce-onderwerp ("${(subject || '').slice(0, 60)}")`;
  }
  return null;
}

const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const AFSPRAAKBELOFTE = /\b(?:we|wij)\s+(?:komen|halen|brengen|bezorgen)|\b(?:wordt|worden)\b[^.!?\n]{0,60}\b(?:opgehaald|teruggebracht|bezorgd)|\bafspraak\b[^.!?\n]{0,50}\b(?:staat|is|gepland)|\b(?:chauffeur|monteur)\s+komt\b/i;
export function heeftAfspraakbelofte(tekst: string): boolean { return AFSPRAAKBELOFTE.test(tekst); }

/** Kalenderdagen in Nederland; relatieve woorden horen bij de conceptdatum. */
export function conceptBlokkade(input: {
  tekst: string; gegenereerdOp?: string | null; now?: Date;
  orders?: Array<{ name?: string; reference?: string; status?: string }>;
}): string | null {
  if (!heeftAfspraakbelofte(input.tekst)) return null;
  const dag = (date: Date) => date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
  const vandaag = dag(input.now ?? new Date());
  const basis = input.gegenereerdOp && Number.isFinite(Date.parse(input.gegenereerdOp))
    ? dag(new Date(input.gegenereerdOp)) : vandaag;
  const datums: string[] = [];
  for (const m of input.tekst.matchAll(/\b(20\d{2})-(\d{2})-(\d{2})\b/g)) datums.push(m[0]);
  for (const m of input.tekst.toLowerCase().matchAll(/\b(\d{1,2})\s+(januari|februari|maart|april|mei|juni|juli|augustus|september|oktober|november|december)(?:\s+(20\d{2}))?\b/g)) {
    datums.push(`${m[3] || basis.slice(0, 4)}-${String(MAANDEN.indexOf(m[2]!) + 1).padStart(2, '0')}-${m[1]!.padStart(2, '0')}`);
  }
  for (const m of input.tekst.matchAll(/\b(\d{1,2})[/\-](\d{1,2})(?:[/\-](20\d{2}))?\b/g)) {
    if (Number(m[1]) < 1 || Number(m[1]) > 31 || Number(m[2]) < 1 || Number(m[2]) > 12) continue;
    datums.push(`${m[3] || basis.slice(0, 4)}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`);
  }
  if (/\b(vandaag|morgen|overmorgen)\b/i.test(input.tekst)) {
    if (!input.gegenereerdOp) return 'De datum van dit afspraakconcept ontbreekt. Beoordeel het antwoord opnieuw.';
    const offset = /\bovermorgen\b/i.test(input.tekst) ? 2 : /\bmorgen\b/i.test(input.tekst) ? 1 : 0;
    const datum = new Date(`${basis}T12:00:00Z`); datum.setUTCDate(datum.getUTCDate() + offset);
    datums.push(dag(datum));
  }
  if (datums.some(d => d < vandaag)) return 'De afspraak in dit concept is verstreken. Beoordeel het antwoord opnieuw.';
  const terminal = input.orders?.find(o => /^(Teruggebracht|Afgerond|Geannuleerd)$/i.test(o.status || ''));
  if (terminal) return `De order ${terminal.name || terminal.reference || ''} staat inmiddels op ${terminal.status}. De afspraakbelofte moet opnieuw worden beoordeeld.`;
  return null;
}

/** Alleen een korte bevestiging/bedankje; vragen en vervolgverzoeken blijven werk. */
export function isBevestigingZonderVraag(tekst: string): boolean {
  const eigen = (tekst.split(/\n(?:Op .+schreef|On .+wrote|Van:|From:|>)/i)[0] || '').trim();
  return eigen.length > 0 && eigen.length < 500 && !/[?]/.test(eigen)
    && /\b(bedankt|dankjewel|dank u|prima|akkoord|tot (?:dan|morgen|zaterdag)|bevestig)\b/i.test(eigen)
    && !/\b(maar|graag|wanneer|waarom|hoe|verzoek|kunt|kunnen|willen|bel|helaas|niet|klacht|schade)\b/i.test(eigen);
}
